import { useRef, useState, useCallback, useEffect } from "react";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import DOMPurify from "dompurify";
import { Send, Paperclip, X, Loader2 } from "lucide-react";
import { openPendingFile } from "../../../utils/download";
import { Spinner } from "@/components/ui/Spinner";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDevIssueComments,
  useAddDevIssueComment,
} from "@/features/dev_issues/hooks/useDevIssues";
import { devIssuesApi } from "@/features/dev_issues/api/devIssues";
import { AttachmentLink } from "@/features/dev_issues/components/AttachmentLink";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import type { IssueAttachment } from "@/features/dev_issues/types";
import type { MentionSuggestion } from "@/components/ui/RichTextEditor";

interface Props {
  issueId: string;
}

// Images plus common document/spreadsheet/presentation/archive/text formats.
// Drag-dropped files report unreliable MIME types (csv may be empty or
// application/vnd.ms-excel), so we match by extension as well.
const ACCEPTED_EXTENSIONS = [
  // images
  "jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "heic", "heif", "avif", "tiff", "tif", "ico",
  // pdf
  "pdf",
  // spreadsheets
  "csv", "tsv", "xls", "xlsx", "xlsm", "ods",
  // documents
  "doc", "docx", "odt", "rtf", "txt", "md",
  // presentations
  "ppt", "pptx", "odp",
  // archives
  "zip", "rar", "7z", "tar", "gz",
];

const FILE_INPUT_ACCEPT = `image/*,.${ACCEPTED_EXTENSIONS.join(",.")}`;

function isAcceptedFile(f: File): boolean {
  if (f.type.startsWith("image/")) return true;
  const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
  return ACCEPTED_EXTENSIONS.includes(ext);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AttachmentList({ attachments }: { attachments: IssueAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <ul className="mt-1.5 flex flex-wrap gap-1.5">
      {attachments.map((a) => (
        <li key={a.id}>
          <AttachmentLink
            attachment={a}
            iconFirst
            iconSize={10}
            className="flex items-center gap-1.5 min-w-0 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-2 py-0.5 rounded"
            nameClassName="max-w-[160px] truncate"
          />
        </li>
      ))}
    </ul>
  );
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function CommentContent({ content }: { content: string }) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const { data: allUsers = [] } = useInternalUsers();
  const names = allUsers.map((u) => u.full_name);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      setLightboxSrc((target as HTMLImageElement).src);
    }
  }, []);

  if (isHtmlContent(content)) {
    return (
      <>
        <div
          className="rich-text-content"
          dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
          onClick={handleClick}
        />
        {lightboxSrc && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setLightboxSrc(null)}
          >
            <img
              src={lightboxSrc}
              alt=""
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 text-white text-2xl font-bold hover:text-gray-300"
              onClick={() => setLightboxSrc(null)}
            >
              ×
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

export function IssueCommentThread({ issueId }: Props) {
  const { data: comments = [], isLoading } = useDevIssueComments(issueId);
  const { mutateAsync: addComment, isPending: sending } = useAddDevIssueComment(issueId);
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const { data: allUsers = [] } = useInternalUsers();

  const mentionSuggestions: MentionSuggestion[] = allUsers.map((u) => ({
    id: u.id,
    name: u.full_name,
    role: u.role,
    is_ceo: u.is_ceo,
  }));

  const [draft, setDraft, clearDraft] = useCommentDraft(`comment-draft-issue-${issueId}`);
  const [composerMentionIds, setComposerMentionIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  async function handleSend() {
    const content = draft.trim();
    if (!content) return;
    try {
      const comment = await addComment({ content, mentionIds: composerMentionIds });
      if (pendingFiles.length > 0) {
        setUploadingFor(comment.id);
        for (const file of pendingFiles) {
          await devIssuesApi.uploadCommentAttachment(issueId, comment.id, file);
        }
        await qc.invalidateQueries({ queryKey: ["dev-issues", issueId, "comments"] });
        setUploadingFor(null);
      }
      clearDraft();
      setComposerMentionIds([]);
      setPendingFiles([]);
    } catch {
      // errors surfaced by query invalidation
    }
  }

  async function handleImageUpload(file: File): Promise<string> {
    return devIssuesApi.uploadCommentImage(issueId, file);
  }

  function addFiles(files: FileList | File[]) {
    const allowed = Array.from(files).filter(isAcceptedFile);
    setPendingFiles((prev) => [...prev, ...allowed]);
  }

  // Native drag/drop listeners on the attach zone. We use addEventListener
  // (not React synthetic handlers) because synthetic onDrop proved unreliable
  // here — the browser kept opening the dropped file instead of firing onDrop.
  // A window-level guard stops the browser from navigating on near-miss drops.
  useEffect(() => {
    const zone = dropZoneRef.current;
    if (!zone) return;
    let counter = 0;

    const onEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      counter += 1;
      setDragOver(true);
    };
    const onOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      e.preventDefault();
      counter -= 1;
      if (counter <= 0) {
        counter = 0;
        setDragOver(false);
      }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      counter = 0;
      setDragOver(false);
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };

    zone.addEventListener("dragenter", onEnter);
    zone.addEventListener("dragover", onOver);
    zone.addEventListener("dragleave", onLeave);
    zone.addEventListener("drop", onDrop);

    // Prevent the browser from opening files dropped outside the zone.
    const preventDefault = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
    };
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);

    return () => {
      zone.removeEventListener("dragenter", onEnter);
      zone.removeEventListener("dragover", onOver);
      zone.removeEventListener("dragleave", onLeave);
      zone.removeEventListener("drop", onDrop);
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = sending || uploadingFor !== null;

  return (
    <div className="flex flex-col gap-3">
      {isLoading && (
        <div className="flex justify-center py-4"><Spinner size="sm" /></div>
      )}

      {!isLoading && comments.length === 0 && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak komentarzy. Napisz pierwszy.</p>
      )}

      {comments.map((c) => {
        const isOwn = currentUser?.id === c.author_id;
        return (
          <div
            key={c.id}
            className="flex gap-3 border-l-4 border-[var(--color-primary)] bg-gray-50 dark:bg-gray-800/60 rounded-r-lg px-3 py-2.5"
          >
            {c.author_photo_url ? (
              <img
                src={c.author_photo_url}
                alt={c.author_name}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[var(--color-primary)] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                {(c.author_name || "?")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{c.author_name}</span>
                {c.is_developer_author && (
                  <span className="text-xs font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                    <span>💻🔥</span>
                    Dev
                  </span>
                )}
                {isOwn && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">Ty</span>
                )}
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto whitespace-nowrap">{formatDate(c.created_at)}</span>
              </div>

              <CommentContent content={c.content} />

              {uploadingFor === c.id && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" /> Przesyłanie załączników…
                </p>
              )}

              <AttachmentList attachments={c.attachments} />
            </div>
          </div>
        );
      })}

      {/* Composer */}
      <div className="flex flex-col gap-2 mt-1 border-t border-gray-100 dark:border-gray-700 pt-3">
        <RichTextEditor
          value={draft}
          onChange={setDraft}
          disabled={busy}
          placeholder="Napisz komentarz… (wpisz @ aby oznaczyć)"
          onImageUpload={handleImageUpload}
          mentionSuggestions={mentionSuggestions}
          onMentionIdsChange={setComposerMentionIds}
        />

        {pendingFiles.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {pendingFiles.map((f, i) => (
              <li
                key={i}
                className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300"
              >
                <button
                  type="button"
                  onClick={() => openPendingFile(f)}
                  className="max-w-[130px] truncate hover:underline hover:text-[var(--color-primary)]"
                  title={`${f.name} — kliknij, aby podejrzeć`}
                >
                  {f.name}
                </button>
                <button
                  onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X size={10} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center justify-between">
          <div ref={dropZoneRef} className="inline-flex rounded-lg">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className={`flex items-center gap-2 text-xs px-4 py-2 rounded-lg border border-dashed transition-colors disabled:opacity-40 ${dragOver ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]" : "border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"}`}
            >
              <Paperclip size={14} className="flex-shrink-0" />
              <span className="whitespace-nowrap">Załącz plik lub przeciągnij tutaj</span>
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={FILE_INPUT_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => e.target.files && addFiles(e.target.files)}
          />

          <button
            onClick={handleSend}
            disabled={busy || !draft.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 transition-colors"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Wyślij
          </button>
        </div>
      </div>
    </div>
  );
}
